import { isStockfishAvailable } from './unified-engine';
import { ensureStockfishReady, getStockfishSource } from './stockfish-engine';

export interface EngineDiagnostics {
  stockfishSupported: boolean;
  stockfishLoaded: boolean;
  stockfishSource?: string;
  webWorkerSupported: boolean;
  lastEngineUsed?: 'stockfish' | 'native';
  errors: string[];
}

let diagnostics: EngineDiagnostics = {
  stockfishSupported: false,
  stockfishLoaded: false,
  webWorkerSupported: typeof Worker !== 'undefined',
  errors: []
};

/**
 * Run comprehensive engine diagnostics
 */
export async function runEngineDiagnostics(): Promise<EngineDiagnostics> {
  const errors: string[] = [];
  
  // Check Web Worker support
  const webWorkerSupported = typeof Worker !== 'undefined';
  if (!webWorkerSupported) {
    errors.push('Web Workers not supported in this environment');
  }

  // Check Stockfish availability
  const stockfishSupported = isStockfishAvailable();
  
  let stockfishLoaded = false;
  let stockfishSource: string | undefined;
  
  if (stockfishSupported && webWorkerSupported) {
    try {
      // Try to prewarm Stockfish
      await ensureStockfishReady();
      stockfishLoaded = true;
      stockfishSource = getStockfishSource();
      console.log(`[Engine Diagnostics] Stockfish loaded successfully from: ${stockfishSource}`);
    } catch (error) {
      errors.push(`Stockfish loading failed: ${error instanceof Error ? error.message : error}`);
      console.error('[Engine Diagnostics] Stockfish loading failed:', error);
    }
  }

  diagnostics = {
    stockfishSupported,
    stockfishLoaded,
    stockfishSource,
    webWorkerSupported,
    errors
  };

  return diagnostics;
}

/**
 * Get current diagnostics without re-running tests
 */
export function getEngineDiagnostics(): EngineDiagnostics {
  return { ...diagnostics };
}

/**
 * Set the last engine used for tracking
 */
export function setLastEngineUsed(engine: 'stockfish' | 'native') {
  diagnostics.lastEngineUsed = engine;
}

/**
 * Add an error to diagnostics
 */
export function addDiagnosticError(error: string) {
  diagnostics.errors.push(error);
}

/**
 * Display engine status to console for debugging
 */
export function logEngineStatus() {
  console.group('[Engine Status]');
  console.log('Web Workers:', diagnostics.webWorkerSupported ? '✅' : '❌');
  console.log('Stockfish Supported:', diagnostics.stockfishSupported ? '✅' : '❌');
  console.log('Stockfish Loaded:', diagnostics.stockfishLoaded ? '✅' : '❌');
  
  if (diagnostics.stockfishSource) {
    console.log('Stockfish Source:', diagnostics.stockfishSource);
  }
  
  if (diagnostics.lastEngineUsed) {
    console.log('Last Engine Used:', diagnostics.lastEngineUsed);
  }
  
  if (diagnostics.errors.length > 0) {
    console.warn('Errors:', diagnostics.errors);
  }
  
  console.groupEnd();
}